package br.com.texeratx.boardgametimeapp.core.spotify.auth

import br.com.texeratx.boardgametimeapp.core.spotify.storage.SpotifyEphemeral
import br.com.texeratx.boardgametimeapp.core.spotify.storage.SpotifyStorage
import br.com.texeratx.boardgametimeapp.core.spotify.storage.SpotifyTokens
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.statement.bodyAsText
import io.ktor.client.request.forms.*
import io.ktor.http.*
import br.com.texeratx.boardgametimeapp.core.utils.currentTimeMillis
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class SpotifyAuthManager(
    private val http: HttpClient,
    private val storage: SpotifyStorage
) {
    private val oauthErrorJson = Json { ignoreUnknownKeys = true }

    fun isConnected(): Boolean = storage.readTokens() != null

    fun buildAuthorizeUrl(): String {
        val verifier = PkceUtil.newCodeVerifier()
        val state = PkceUtil.newState()
        val challenge = PkceUtil.codeChallengeS256(verifier)

        storage.saveEphemeral(SpotifyEphemeral(state = state, codeVerifier = verifier))

        val scopeParam = SpotifyScopes.REQUIRED.joinToString(" ")
        val url = URLBuilder(SpotifyConfig.AUTH_URL).apply {
            parameters.append("response_type", "code")
            parameters.append("client_id", SpotifyConfig.CLIENT_ID)
            parameters.append("redirect_uri", SpotifyConfig.REDIRECT_URI)
            parameters.append("code_challenge_method", "S256")
            parameters.append("code_challenge", challenge)
            parameters.append("state", state)
            parameters.append("scope", scopeParam)
            parameters.append("show_dialog", "true")
        }.buildString()

        return url
    }

    /**
     * Recebe a URL completa do redirect (com ?code=...&state=...)
     * Faz exchange e salva tokens.
     */
    suspend fun handleRedirect(redirectUrl: String): Result<Unit> = runCatching {
        val url = Url(redirectUrl)
        val code = url.parameters["code"]
        val stateFromUrl = url.parameters["state"]

        val eph = storage.readEphemeral() ?: error("Spotify: estado expirado (ephemeral vazio)")
        if (code.isNullOrBlank() || stateFromUrl.isNullOrBlank() || stateFromUrl != eph.state) {
            storage.clearEphemeral()
            error("Spotify: login cancelado/estado inválido")
        }

        val tokenResp: SpotifyTokenResponse = http.submitForm(
            url = SpotifyConfig.TOKEN_URL,
            formParameters = Parameters.build {
                append("grant_type", "authorization_code")
                append("client_id", SpotifyConfig.CLIENT_ID)
                append("code", code)
                append("redirect_uri", SpotifyConfig.REDIRECT_URI)
                append("code_verifier", eph.codeVerifier)
            }
        ).body()

        val now = currentTimeMillis() / 1000L

        val expiresAt = now + tokenResp.expiresIn - 30 // margem

        val scopes = tokenResp.scope
            .split(' ')
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .toSet()

        storage.saveTokens(
            SpotifyTokens(
                accessToken = tokenResp.accessToken,
                refreshToken = tokenResp.refreshToken,
                expiresAtEpochSec = expiresAt,
                scope = scopes
            )
        )

        storage.clearEphemeral()
    }

    fun getAccessTokenOrNull(): String? {
        return storage.readTokens()?.accessToken
    }

    fun getRefreshTokenOrNull(): String? {
        return storage.readTokens()?.refreshToken
    }

    suspend fun getValidAccessTokenOrNull(): String? {
        val tokens = storage.readTokens() ?: return null

        val now = currentTimeMillis() / 1000L

        val isExpired = now >= tokens.expiresAtEpochSec

        return if (!isExpired) {
            tokens.accessToken
        } else {
            forceRefreshTokenOrNull()
        }
    }

    suspend fun forceRefreshTokenOrNull(): String? {
        val tokens = storage.readTokens() ?: return null
        val refreshToken = tokens.refreshToken ?: return null

        val response = http.submitForm(
            url = SpotifyConfig.TOKEN_URL,
            formParameters = Parameters.build {
                append("grant_type", "refresh_token")
                append("client_id", SpotifyConfig.CLIENT_ID)
                append("refresh_token", refreshToken)
            }
        )

        if (!response.status.isSuccess()) {
            val rawBody = runCatching { response.bodyAsText() }.getOrNull().orEmpty()
            val oauthError = runCatching {
                oauthErrorJson.decodeFromString<SpotifyOAuthErrorResponse>(rawBody)
            }.getOrNull()

            if (oauthError?.error == "invalid_grant") {
                storage.clearTokens()
                throw SpotifyAuthException(
                    reason = SpotifyAuthException.Reason.RECONNECT_REQUIRED,
                    message = oauthError.errorDescription ?: "Spotify exige reconexão"
                )
            }

            throw SpotifyAuthException(
                reason = SpotifyAuthException.Reason.GENERIC_AUTH_FAILURE,
                message = oauthError?.errorDescription
                    ?: "Falha ao atualizar autenticação do Spotify (HTTP ${response.status.value})"
            )
        }

        val tokenResp: SpotifyRefreshResponse = try {
            response.body()
        } catch (_: Throwable) {
            storage.clearTokens()
            throw SpotifyAuthException(
                reason = SpotifyAuthException.Reason.RECONNECT_REQUIRED,
                message = "Resposta inválida do Spotify. Reconecte sua conta."
            )
        }

        val now = currentTimeMillis() / 1000L

        val expiresAt = now + tokenResp.expiresIn - 30

        // Spotify pode não devolver refresh_token no refresh. Então mantemos o antigo.
        storage.saveTokens(
            tokens.copy(
                accessToken = tokenResp.accessToken,
                expiresAtEpochSec = expiresAt,
                // se vier scope vazio/nulo, mantém o antigo
                scope = tokenResp.scope?.split(' ')?.map { it.trim() }?.filter { it.isNotBlank() }?.toSet()
                    ?: tokens.scope
            )
        )

        return tokenResp.accessToken
    }

    @Serializable
    private data class SpotifyOAuthErrorResponse(
        val error: String? = null,
        @SerialName("error_description")
        val errorDescription: String? = null
    )
}
